<script setup>
import {useBaseStore} from "~/stores/base";
import {Navigation, Pagination} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import star10Percent from "assets/img/icon-star-10-percent.svg";
import star20Percent from "assets/img/icon-star-20-percent.svg";
import star30Percent from "assets/img/icon-star-30-percent.svg";
import star40Percent from "assets/img/icon-star-40-percent.svg";
import star50Percent from "assets/img/icon-star-50-percent.svg";
import star60Percent from "assets/img/icon-star-60-percent.svg";
import star70Percent from "assets/img/icon-star-70-percent.svg";
import star80Percent from "assets/img/icon-star-80-percent.svg";
import star90Percent from "assets/img/icon-star-90-percent.svg";
import star100Percent from "assets/img/icon-star-filled.svg";

const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(['show'])

/** props **/
const props = defineProps({
  observerActive: {
    default: true,
    required: true
  },
  review: {
    default: {},
    required: true
  },
  index: {
    default: 0,
    required: true
  }
})

/** data **/
const reviewRef = ref(null)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const fullStars = computed(() => Math.floor(props.review.score || 0))
const hasHalfStar = computed(() => props.review.score % 1 >= 0.5)
const halfStarImg = computed(() => {
  const percent = Math.round(props.review.score % 1 * 10) * 10
  switch (percent) {
    case 10:
      return star10Percent
    case 20:
      return star20Percent
    case 30:
      return star30Percent
    case 40:
      return star40Percent
    case 50:
      return star50Percent
    case 60:
      return star60Percent
    case 70:
      return star70Percent
    case 80:
      return star80Percent
    case 90:
      return star90Percent
    case 100:
      return star100Percent
  }
})
const emptyStars = computed(() => 5 - fullStars.value - (hasHalfStar.value ? 1 : 0))

/** 初始化观察者 **/
const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      // console.log(`元素 ${props.index} 出现在视口中`);
      // console.log(`部分可见，比例: ${entry.intersectionRatio}`);
      if (entry.intersectionRatio >= 0.5) {
        // console.log(`完全可见`);
        // 可以在这里执行其他操作
        emits('show', props.index)
      }
    }
  });
}, {
  // threshold: [0, 1] // 部分可见和完全可见都触发
  threshold: [0.5] // 完全可见触发
});

/** watch **/
watch(() => props.observerActive, () => {
  if (props.observerActive) {
    observer.observe(reviewRef.value);
  } else {
    observer.disconnect();
  }
})

/** methods **/
const onSwiper = (swiper) => {
};
const onSlideChange = async () => {
};

/** lifecycle **/
onMounted(() => {
  if (props.observerActive) observer.observe(reviewRef.value);
})

</script>

<template>
  <div class="module-review-mobile" ref="reviewRef" v-if="isMobile">
    <div class="top">
      <div class="username">{{ review.userInfo.userName }}</div>
      <div class="userinfo">
        <div class="detail">
          <div class="label">Size</div>
          <div class="value">{{ review.userInfo.sizeRange }}</div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Body Type</div>
          <div class="value">{{ review.userInfo.bodyType }}</div>
        </div>
        <div class="detail">
          <div class="label">Height</div>
          <div class="value">{{
              review.userInfo.heightInch === "-" ? review.userInfo.heightInch : `${review.userInfo.heightFt}’${review.userInfo.heightInch}”`
            }}
          </div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Bra Size</div>
          <div class="value">{{ review.userInfo.braSize }}</div>
        </div>
        <div class="detail">
          <div class="label">Weight</div>
          <div class="value">{{ review.userInfo.weightIbs }}</div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Age</div>
          <div class="value">{{ review.userInfo.age }}</div>
        </div>
      </div>
    </div>
    <div class="center">
      <div class="rate">
        <div class="stars">
          <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="(star, index) in fullStars" :key="index">
          <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
          <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="(star, index) in emptyStars" :key="index">
        </div>
        <div class="createTime">
          {{ review.createTime }}
        </div>
      </div>
      <div class="title">
        {{ review.title }}
      </div>
      <div class="description">
        {{ review.description }}
      </div>
    </div>
    <div class="bottom" v-if="review.imageSets.length">
      <swiper
        loop
        grab-cursor
        :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
        :pagination="{el: '.swiper-pagination', bulletActiveClass: 'my-bullet-active'}"
        :modules="[Pagination]"
        :slides-per-view="1"
        :slides-per-group="1"
        :space-between="0"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(item, index) in review.imageSets" :key="index">
          <img class="img" :src="item.url" alt="image">
        </swiper-slide>

        <!-- swiper-pagination -->
        <div class="swiper-pagination"/>
      </swiper>
    </div>
  </div>
  <div class="module-review-desktop" ref="reviewRef" v-else>
    <div class="top">
      <div class="username">{{ review.userInfo.userName }}</div>
      <div class="userinfo">
        <div class="detail">
          <div class="label">Size</div>
          <div class="value">{{ review.userInfo.sizeRange }}</div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Body Type</div>
          <div class="value">{{ review.userInfo.bodyType }}</div>
        </div>
        <div class="detail">
          <div class="label">Height</div>
          <div class="value">{{
              review.userInfo.heightInch === "-" ? review.userInfo.heightInch : `${review.userInfo.heightFt}’${review.userInfo.heightInch}”`
            }}
          </div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Bra Size</div>
          <div class="value">{{ review.userInfo.braSize }}</div>
        </div>
        <div class="detail">
          <div class="label">Weight</div>
          <div class="value">{{ review.userInfo.weightIbs }}</div>
        </div>
        <div class="detail">
          <div class="label" style="width: 70px">Age</div>
          <div class="value">{{ review.userInfo.age }}</div>
        </div>
      </div>
    </div>
    <div class="center">
      <div class="rate">
        <div class="stars">
          <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in fullStars">
          <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
          <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in emptyStars">
        </div>
        <div class="createTime">
          {{ review.createTime }}
        </div>
      </div>
      <div class="title">
        {{ review.title }}
      </div>
      <div class="description">
        {{ review.description }}
      </div>
    </div>
    <div class="bottom" v-if="review.imageSets.length">
      <swiper
        loop
        grab-cursor
        :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
        :pagination="{el: '.swiper-pagination', bulletActiveClass: 'my-bullet-active'}"
        :modules="[Navigation, Pagination]"
        :slides-per-view="1"
        :slides-per-group="1"
        :space-between="0"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(item, index) in review.imageSets" :key="index">
          <img class="img" :src="item.url" alt="image">
        </swiper-slide>

        <!-- swiper-pagination -->
        <div class="swiper-pagination"/>

        <!-- If we need navigation buttons -->
        <div class="navigation-left" v-if="review.imageSets.length > 1">
          <img src="@/assets/img/icon-left-small.svg" alt="left">
        </div>
        <div class="navigation-right" v-if="review.imageSets.length > 1">
          <img src="@/assets/img/icon-right-small.svg" alt="right">
        </div>
      </swiper>
    </div>
  </div>
</template>
<style lang="scss">
@import "src/assets/config";

.module-review-mobile {
  .swiper {
    .swiper-pagination {
      bottom: 12px !important;

      .swiper-pagination-bullet {
        width: 12px;
        height: 12px;
      }

      .my-bullet-active {
        background-color: $color-gray-800;
        opacity: 1;
      }
    }
  }
}

.module-review-desktop {
  .swiper {
    .swiper-pagination {
      bottom: 12px !important;

      .swiper-pagination-bullet {
        width: 12px;
        height: 12px;
      }

      .my-bullet-active {
        background-color: $color-gray-800;
        opacity: 1;
      }
    }
  }
}
</style>
<style scoped lang="scss">
@import "src/assets/config";

.module-review-mobile {
  padding: 16px;
  box-shadow: 0 0 0 0.5px $color-gray-300;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .top {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .username {
      text-overflow: ellipsis;
      /* Text md/Bold */
      font-family: "TWK Lausanne";
      font-size: 16px;
      font-style: normal;
      font-weight: 650;
      line-height: 24px; /* 150% */
    }

    .userinfo {
      background: $color-gray-50;
      padding: 8px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .detail {
        width: 48%;

        display: flex;
        align-items: center;

        .label {
          text-overflow: ellipsis;
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          width: 50px;
        }

        .value {
          text-overflow: ellipsis;
          /* Text xs/Bold */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */
        }
      }
    }
  }

  .center {
    .rate {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .stars {
      }

      .createTime {
        /* Text xs/Regular */
        font-family: "TWK Lausanne";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 150% */
      }
    }

    .title {
      margin-top: 16px;
      /* Text lg/Bold */
      font-family: "TWK Lausanne";
      font-size: 18px;
      font-style: normal;
      font-weight: 650;
      line-height: 28px; /* 155.556% */

      overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
      word-break: break-word;    /* 提高兼容性（非标准，WebKit 支持） */
      white-space: pre-wrap;     /* 保留用户输入的换行符 */
    }

    .description {
      margin-top: 6px;
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */

      overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
      word-break: break-word;    /* 提高兼容性（非标准，WebKit 支持） */
      white-space: pre-wrap;     /* 保留用户输入的换行符 */
    }
  }

  .bottom {
    .swiper {
      position: relative;
      height: fit-content;

      .swiper-slide {
        display: flex;

        .img {
          width: 100%;
          height: 130vw;
          background-color: $color-gray-100;
          object-fit: contain;
          object-position: center center;
        }
      }

      .my-bullet-active {
        background-color: $color-gray-800;
        opacity: 1;
      }

      .navigation-left {
        position: absolute;
        display: flex;
        left: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -50%);
        cursor: pointer;

        img {
          width: 30px;
          height: 30px;
        }
      }

      .navigation-right {
        position: absolute;
        display: flex;
        right: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -50%);
        cursor: pointer;

        img {
          width: 30px;
          height: 30px;
        }
      }
    }
  }
}

.module-review-desktop {
  padding: 16px;
  box-shadow: 0 0 0 0.5px $color-gray-300;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .top {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .username {
      text-overflow: ellipsis;
      /* Text md/Bold */
      font-family: "TWK Lausanne";
      font-size: 16px;
      font-style: normal;
      font-weight: 650;
      line-height: 24px; /* 150% */
    }

    .userinfo {
      background: $color-gray-50;
      padding: 8px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .detail {
        width: 48%;

        display: flex;
        align-items: center;

        .label {
          text-overflow: ellipsis;
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          width: 50px;
        }

        .value {
          text-overflow: ellipsis;
          /* Text xs/Bold */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */
        }
      }
    }
  }

  .center {
    .rate {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .stars {
      }

      .createTime {
        /* Text xs/Regular */
        font-family: "TWK Lausanne";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 150% */
      }
    }

    .title {
      margin-top: 16px;
      /* Text lg/Bold */
      font-family: "TWK Lausanne";
      font-size: 18px;
      font-style: normal;
      font-weight: 650;
      line-height: 28px; /* 155.556% */

      overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
      word-break: break-word;    /* 提高兼容性（非标准，WebKit 支持） */
      white-space: pre-wrap;     /* 保留用户输入的换行符 */
    }

    .description {
      margin-top: 6px;
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */

      overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
      word-break: break-word;    /* 提高兼容性（非标准，WebKit 支持） */
      white-space: pre-wrap;     /* 保留用户输入的换行符 */
    }
  }

  .bottom {
    .swiper {
      position: relative;
      height: fit-content;

      &:hover {
        .navigation-left {
          opacity: 1;
        }

        .navigation-right {
          opacity: 1;
        }
      }

      .swiper-slide {
        display: flex;

        .img {
          width: 100%;
          height: 488px;
          background-color: $color-gray-100;
          object-fit: contain;
          object-position: center center;
        }
      }

      .my-bullet-active {
        background-color: $color-gray-800;
        opacity: 1;
      }

      .navigation-left {
        position: absolute;
        display: flex;
        left: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -50%);
        cursor: pointer;

        transition: all .1s ease-in-out;
        opacity: 0;

        img {
          width: 30px;
          height: 30px;
        }
      }

      .navigation-right {
        position: absolute;
        display: flex;
        right: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -50%);
        cursor: pointer;

        transition: all .1s ease-in-out;
        opacity: 0;

        img {
          width: 30px;
          height: 30px;
        }
      }
    }
  }
}
</style>
